import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';
import moment from 'moment';
import { Grid, Tab, Tabs } from '@mui/material';
import { usePostHog } from 'posthog-js/react';
import TabPanel from '../../common/ui/TabPanel';
import { DashboardService } from '../../api';
import { SummaryTab } from './summary/SummaryTab';
import { TableTab } from './table/TableTab';
import { AnalyticsFilterProvider } from './table/AnalyticsFilter/AnalyticsFilterContext';
import { mb } from '../../common/theme/mbTokens';

export const extendedDashboardFetcher = async () => DashboardService.extendedDashboard();

const TAB_KEYS = ['summary', 'activity'] as const;
type TabKey = (typeof TAB_KEYS)[number];

const readInitialTab = (): number => {
  const hash = window.location.hash.replace('#', '') as TabKey;
  const idx = TAB_KEYS.indexOf(hash);
  return idx >= 0 ? idx : 0;
};

export const AnalyticsPage = () => {
  const { t } = useTranslation();
  const posthog = usePostHog();
  const [lastFetch, setLastFetch] = useState<moment.Moment>(moment());
  const [isRefetching, setIsRefetching] = useState(false);
  const [tabIndex, setTabIndex] = useState<number>(readInitialTab);

  const {
    data,
    isLoading: swrLoading,
    mutate: refetch,
  } = useSWR({ opId: 'getExtendedDashboard' }, extendedDashboardFetcher, {
    revalidateOnFocus: false,
    refreshInterval: 0,
  });

  const handleRefresh = async () => {
    setIsRefetching(true);
    await refetch();
    setLastFetch(moment());
    setIsRefetching(false);
  };

  const isLoading = swrLoading || isRefetching;

  const handleChange = (_: unknown, newValue: number) => {
    setTabIndex(newValue);
    window.location.hash = `#${TAB_KEYS[newValue]}`;
  };

  useEffect(() => {
    const onHashChange = () => setTabIndex(readInitialTab());
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  useEffect(() => {
    posthog.capture('Analytics tab viewed', { tab: TAB_KEYS[tabIndex] });
  }, [posthog, tabIndex]);

  const isSummaryTab = tabIndex === 0;

  return (
    <AnalyticsFilterProvider>
      <Grid
        container
        direction="column"
        sx={{ height: '100%', flexWrap: 'nowrap', overflowY: isSummaryTab ? 'auto' : undefined }}>
        <Grid item sx={{ px: mb.layout.cardPadding, pt: mb.layout.cardPadding, pb: 2 }}>
          <Tabs value={tabIndex} onChange={handleChange} aria-label="analytics tabs">
            {TAB_KEYS.map((key) => (
              <Tab key={key} label={t(`tabs.${key}`)} />
            ))}
          </Tabs>
        </Grid>
        <Grid
          item
          sx={{
            ...(isSummaryTab ? { flex: '1 0 auto' } : { flex: 1, minHeight: 0, overflow: 'auto' }),
            display: 'flex',
            flexDirection: 'column',
          }}>
          <TabPanel value={tabIndex} index={0}>
            <SummaryTab data={data} isLoading={isLoading} lastFetch={lastFetch} onRefresh={handleRefresh} />
          </TabPanel>
          <TabPanel value={tabIndex} index={1}>
            <TableTab data={data} isLoading={isLoading} lastFetch={lastFetch} onRefresh={handleRefresh} />
          </TabPanel>
        </Grid>
      </Grid>
    </AnalyticsFilterProvider>
  );
};
